import React, { useEffect, useMemo, useState } from 'react'

function Frame({ title, trains, onBack, children }) {
  return (
    <div className="flex flex-col gap-4 text-white">
      <div className="flex items-center justify-between gap-3">
        <button type="button" onClick={onBack} className="text-sm text-slate-400 hover:text-white">
          ← Volver
        </button>
        <span className="text-[11px] uppercase tracking-widest text-cyan-300/80">{trains}</span>
      </div>
      <h2 className="text-2xl font-black">{title}</h2>
      {children}
    </div>
  )
}

function EndCard({ title, line, score, onBack }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-white/5 p-6 text-center">
      <p className="text-4xl font-black text-cyan-200">{score}</p>
      <p className="mt-3 text-lg font-semibold">{title}</p>
      <p className="mt-2 text-sm text-slate-300 leading-relaxed">{line}</p>
      <button type="button" onClick={onBack} className="mt-6 rounded-full bg-cyan-400 px-5 py-3 text-sm font-black text-slate-950">
        Listo
      </button>
    </div>
  )
}

const PULSO = [0, 2, 4, 1, 3, 5]

export function PulsoGame({ onBack, onComplete }) {
  const [round, setRound] = useState(0)
  const [step, setStep] = useState(0)
  const [phase, setPhase] = useState('show')
  const [picked, setPicked] = useState([])
  const [score, setScore] = useState(0)
  const seq = useMemo(() => PULSO.slice(0, 3 + round), [round])

  useEffect(() => {
    if (phase !== 'show') return undefined
    if (step >= seq.length) {
      const t = setTimeout(() => { setPhase('play'); setStep(0) }, 280)
      return () => clearTimeout(t)
    }
    const t = setTimeout(() => setStep((n) => n + 1), 520)
    return () => clearTimeout(t)
  }, [phase, step, seq])

  const tap = (i) => {
    if (phase !== 'play') return
    const next = [...picked, i]
    if (seq[next.length - 1] !== i) {
      setPhase('end')
      onComplete?.(score)
      return
    }
    setPicked(next)
    if (next.length === seq.length) {
      const gained = seq.length * 8
      setScore((s) => s + gained)
      if (round >= 3) {
        setPhase('end')
        onComplete?.(score + gained)
      } else {
        setRound((r) => r + 1)
        setPicked([])
        setStep(0)
        setPhase('show')
      }
    }
  }

  if (phase === 'end') {
    return (
      <Frame title="Pulso" trains="Atención" onBack={onBack}>
        <EndCard title="La órbita se cierra" line="Anticipar el siguiente pulso es atención, no reflejo. Esta ronda ya terminó." score={score} onBack={onBack} />
      </Frame>
    )
  }

  const lit = phase === 'show' ? seq[step - 1] : null
  return (
    <Frame title="Pulso" trains="Atención" onBack={onBack}>
      <p className="text-sm text-slate-300">{phase === 'show' ? 'Mira la órbita.' : 'Repite el pulso.'}</p>
      <div className="relative mx-auto h-64 w-64">
        {PULSO.map((i) => {
          const ang = (i / 6) * Math.PI * 2 - Math.PI / 2
          const x = 112 + Math.cos(ang) * 92
          const y = 112 + Math.sin(ang) * 92
          const on = lit === i
          return (
            <button
              key={i}
              type="button"
              aria-label={`Nodo ${i + 1}`}
              onClick={() => tap(i)}
              className="absolute h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-300/40"
              style={{ left: x, top: y, background: on ? '#67e8f9' : 'rgba(34,211,238,0.12)' }}
            />
          )
        })}
      </div>
    </Frame>
  )
}

const SHADOW_ROUNDS = [
  [0, 4, 8],
  [1, 3, 5, 7],
  [0, 2, 4, 6, 8],
  [2, 4, 5, 6],
  [0, 1, 5, 8],
]

export function SombraGame({ onBack, onComplete }) {
  const [round, setRound] = useState(0)
  const [show, setShow] = useState(true)
  const [picked, setPicked] = useState([])
  const [score, setScore] = useState(0)
  const cells = SHADOW_ROUNDS[round]

  useEffect(() => {
    setShow(true)
    setPicked([])
    const t = setTimeout(() => setShow(false), 900)
    return () => clearTimeout(t)
  }, [round])

  const tap = (i) => {
    if (show) return
    const next = picked.includes(i) ? picked.filter((n) => n !== i) : [...picked, i]
    setPicked(next)
    if (next.length !== cells.length) return
    const ok = cells.every((n) => next.includes(n))
    const gained = ok ? 20 : 0
    const total = score + gained
    setScore(total)
    if (round >= SHADOW_ROUNDS.length - 1) {
      onComplete?.(total)
      setRound(-1)
    } else {
      setRound((r) => r + 1)
    }
  }

  if (round < 0) {
    return (
      <Frame title="Sombra" trains="Memoria espacial" onBack={onBack}>
        <EndCard title="La imagen se quedó" line="Reconstruir lo que ya no ves entrena la memoria de trabajo. No hay ronda extra." score={score} onBack={onBack} />
      </Frame>
    )
  }

  return (
    <Frame title="Sombra" trains="Memoria espacial" onBack={onBack}>
      <p className="text-sm text-slate-300">{show ? 'Memoriza las celdas.' : 'Tócalas.'}</p>
      <div className="mx-auto grid w-64 grid-cols-3 gap-2">
        {Array.from({ length: 9 }, (_, i) => {
          const on = show ? cells.includes(i) : picked.includes(i)
          return (
            <button key={i} type="button" aria-label={`Celda ${i + 1}`} onClick={() => tap(i)} className="h-20 rounded-2xl border border-white/10" style={{ background: on ? '#34d399' : 'rgba(255,255,255,0.04)' }} />
          )
        })}
      </div>
    </Frame>
  )
}

const FILTERS = [
  { honest: 'Un estudio de 200 personas vio mejor sueño al escribir el día.', trap: 'Esto cura el insomnio en todos los casos.', why: '“Todos” y “cura” prometen más de lo que un estudio puede decir.' },
  { honest: 'Algunas personas gastan menos cuando ven el gasto del mes.', trap: 'Esta app te hará rico en 7 días.', why: 'Un plazo mágico no es un resultado.' },
  { honest: 'Caminar 20 minutos puede mejorar el ánimo de muchos adultos.', trap: 'Caminar elimina la depresión para siempre.', why: '“Para siempre” borra el matiz.' },
  { honest: 'Repetir una melodía corta ejercita la memoria auditiva.', trap: 'Si no aciertas, tu memoria está dañada.', why: 'Un juego no diagnostica.' },
  { honest: 'Dos personas pueden repartir la casa con reglas claras.', trap: 'Si discuten, la relación ya terminó.', why: 'Una pelea no es un veredicto.' },
  { honest: 'Dormir mal una noche no define tu semana.', trap: 'Una mala noche arruina tu cerebro.', why: 'El miedo absoluto no es información.' },
]

export function FiltroGame({ onBack, onComplete }) {
  const [i, setI] = useState(0)
  const [score, setScore] = useState(0)
  const [why, setWhy] = useState('')
  const card = FILTERS[i]

  const choose = (honest) => {
    const gained = honest ? 15 : 0
    const total = score + gained
    setScore(total)
    setWhy(card.why)
    setTimeout(() => {
      setWhy('')
      if (i >= FILTERS.length - 1) {
        onComplete?.(total)
        setI(-1)
      } else setI((n) => n + 1)
    }, 900)
  }

  if (i < 0) {
    return (
      <Frame title="Filtro" trains="Lectura crítica" onBack={onBack}>
        <EndCard title="Seis titulares, y se acaba" line="La frase cuidadosa casi nunca usa “todos”, “cura” o “para siempre”." score={score} onBack={onBack} />
      </Frame>
    )
  }

  return (
    <Frame title="Filtro" trains="Lectura crítica" onBack={onBack}>
      <p className="text-sm text-slate-300">Elige la frase que se puede defender.</p>
      <div className="grid gap-3">
        {[card.honest, card.trap].sort().map((text) => (
          <button key={text} type="button" onClick={() => choose(text === card.honest)} className="rounded-2xl border border-white/10 bg-white/5 p-4 text-left text-sm leading-relaxed">
            {text}
          </button>
        ))}
      </div>
      {why ? <p className="text-sm text-amber-200">{why}</p> : null}
    </Frame>
  )
}

const ANCHORS = [
  { sentence: 'El ritmo de la frase se llama ___.', options: ['cadencia', 'hipoteca', 'nébula'], answer: 'cadencia' },
  { sentence: 'Guardar un poco cada quincena es un ___.', options: ['hábito', 'eclipse', 'verso'], answer: 'hábito' },
  { sentence: 'Recordar cuatro notas y repetirlas usa la memoria de ___.', options: ['trabajo', 'mármol', 'aduana'], answer: 'trabajo' },
  { sentence: 'Dos personas en una casa necesitan reglas, no ___.', options: ['suposiciones', 'ventanas', 'cucharas'], answer: 'suposiciones' },
  { sentence: 'Un sueño escrito al despertar conserva más ___.', options: ['detalle', 'impuesto', 'batería'], answer: 'detalle' },
  { sentence: 'Parar a tiempo también es una forma de ___.', options: ['aprender', 'endeudarse', 'gritar'], answer: 'aprender' },
]

export function AnclaGame({ onBack, onComplete }) {
  const [i, setI] = useState(0)
  const [score, setScore] = useState(0)
  const card = ANCHORS[i]

  const choose = (word) => {
    const total = score + (word === card.answer ? 12 : 0)
    setScore(total)
    if (i >= ANCHORS.length - 1) {
      onComplete?.(total)
      setI(-1)
    } else setI((n) => n + 1)
  }

  if (i < 0) {
    return (
      <Frame title="Ancla" trains="Lenguaje" onBack={onBack}>
        <EndCard title="La palabra justa" line="Elegir el término preciso es pensar, no adivinar. Esta sesión ya cumplió." score={score} onBack={onBack} />
      </Frame>
    )
  }

  return (
    <Frame title="Ancla" trains="Lenguaje" onBack={onBack}>
      <p className="text-lg leading-relaxed">{card.sentence}</p>
      <div className="grid gap-2">
        {card.options.map((word) => (
          <button key={word} type="button" onClick={() => choose(word)} className="rounded-full border border-white/10 px-4 py-3 text-left">
            {word}
          </button>
        ))}
      </div>
    </Frame>
  )
}

export function CierreGame({ onBack, onComplete }) {
  const [left, setLeft] = useState(20)
  const [score, setScore] = useState(0)
  const [target, setTarget] = useState(0)
  const [done, setDone] = useState(false)
  const scoreRef = React.useRef(0)
  const doneRef = React.useRef(false)
  const onCompleteRef = React.useRef(onComplete)
  onCompleteRef.current = onComplete

  useEffect(() => {
    const t = setInterval(() => {
      setTarget(Math.floor(Math.random() * 6))
      setLeft((n) => {
        if (n <= 1) {
          if (!doneRef.current) {
            doneRef.current = true
            setDone(true)
            onCompleteRef.current?.(scoreRef.current)
          }
          return 0
        }
        return n - 1
      })
    }, 700)
    return () => clearInterval(t)
  }, [])

  if (done) {
    return (
      <Frame title="Cierre" trains="Foco con final" onBack={onBack}>
        <EndCard title="Se acabó el tiempo a propósito" line="El juego no continúa. Esos segundos fueron atención real, no otra ronda infinita." score={score} onBack={onBack} />
      </Frame>
    )
  }

  return (
    <Frame title="Cierre" trains="Foco con final" onBack={onBack}>
      <p className="text-sm text-slate-300">Toca solo el punto claro. Quedan {left}s y luego se cierra.</p>
      <div className="grid grid-cols-3 gap-3">
        {Array.from({ length: 6 }, (_, i) => (
          <button
            key={i}
            type="button"
            aria-label={i === target ? 'Punto claro' : 'Punto tenue'}
            onClick={() => {
              if (i !== target || doneRef.current) return
              scoreRef.current += 5
              setScore(scoreRef.current)
            }}
            className="h-20 rounded-2xl"
            style={{ background: i === target ? '#e0f2fe' : 'rgba(148,163,184,0.25)' }}
          />
        ))}
      </div>
    </Frame>
  )
}
